import { Component, forwardRef, Inject, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
	CkEditorDynamicScriptLoaderServiceInterface,
	DYNAMIC_SCRIPT_LOADER_INJECTION_TOKEN,
	LuCkEditorCustomPlugin,
	LuCkeditorOptions,
	LuInternalCkEditorOptions
} from './lu-ckeditor.models';
import { generateComponentId } from './utils/global.utils';
import { loadPlugins } from './utils/plugins/plugins-add.util';
import { removePlugins } from './utils/plugins/plugins-remove.util';

import { defaultToolbar } from './utils/toolbar/default.const';
import { forumsToolbar } from './utils/toolbar/forums-toolbar.util';
import { insertInToolbar } from './utils/toolbar/insert.util';
import { postCommentsToolbar } from './utils/toolbar/post-comments.util';
import { sessionDescriptionToolbar } from './utils/toolbar/session-description.util';

declare var CKEDITOR: any;

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'lu-ckeditor',
	templateUrl: './lu-ckeditor.component.html',
	styleUrls: ['./lu-ckeditor.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LuCkeditorComponent),
			multi: true
		}
	]
})
export class LuCkeditorComponent implements OnInit, ControlValueAccessor {
	ckEditorOptions: LuInternalCkEditorOptions = {
		toolbar: defaultToolbar,
		extraPlugins: '',
		bodyClass: '',
		format_tags: 'p;h1;h2;h3;h4;h5;h6;pre;address;div',
		removeDialogTabs: 'link:upload;image:Upload'
	};

	@Input() id: string = generateComponentId();
	@Input() options: LuCkeditorOptions = {} as LuCkeditorOptions;

	constructor(
		@Inject(DYNAMIC_SCRIPT_LOADER_INJECTION_TOKEN)
		public dynamicScriptLoader: CkEditorDynamicScriptLoaderServiceInterface
	) {}

	ngOnInit(): void {
		this.dynamicScriptLoader.instancesIds.push(this.id);
		this.dynamicScriptLoader.load('ck-editor').then(data => {
			this.setOptions();

			// Create and initialize a CKEditor instance
			CKEDITOR.replace(this.id, this.ckEditorOptions);

			// Set initial value (comes from formly wrapper)
			if (this.options.value) {
				CKEDITOR.instances[this.id].setData(this.options.value);
			}

			// Register editorTextChangeEvent callback to be called on CK editor text change
			if (this.options.editorTextChangeEvent) {
				CKEDITOR.instances[this.id].on('change', this.options.editorTextChangeEvent);
			} else {
				CKEDITOR.instances[this.id].on('change', event => {
					if (event && event.editor) {
						this.onChange(event.editor.getData());
						this.onTouched();
					}
				});
			}

			if (this.options.onReady) {
				CKEDITOR.instances[this.id].on('instanceReady', this.options.onReady);
			}

			// Overrides window to set custom configuration for executeDjImageUpload
			window.executeDjImageUpload = this.options.executeDjImageUpload || (() => {});

			if (this.options?.customPlugins?.length) {
				this.addCustomPlugin(this.options.customPlugins);
			}

			CKEDITOR.instances[this.id].on('instanceReady', this.configureCustomFields);
		});
	}

	/**
	 * Configure custom fields styling
	 */
	private configureCustomFields(): void {
		const ckeButtons = document.querySelectorAll('.cke_button');
		ckeButtons.forEach(button => {
			const label = button.querySelector('.cke_button_label') as HTMLElement;
			const icon = button.querySelector('.cke_button_icon') as HTMLElement;

			let bgImage = window.getComputedStyle(icon).getPropertyValue('background-image');

			if (!bgImage || bgImage === 'none') {
				label.style.display = 'block';
				icon.style.display = 'none';
			}
		});
	}

	setOptions(): void {
		CKEDITOR.timestamp = Math.random();

		if (this.options.clearAllPreviousInstances && CKEDITOR.instances) {
			this.clearInstances();
		}

		if (this.options.width) {
			this.ckEditorOptions.width = this.options.width;
		}

		if (this.options.height) {
			this.ckEditorOptions.height = this.options.height;
		}

		// TODO: Should be removed in the future
		if (this.options.toolbarForForums) {
			this.ckEditorOptions.linkShowAdvancedTab = false;
			this.ckEditorOptions.toolbar = forumsToolbar;
		}

		// TODO: Should be removed in the future
		if (this.options.toolbarForPostComments) {
			this.ckEditorOptions.linkShowAdvancedTab = false;
			this.ckEditorOptions.toolbar = postCommentsToolbar;
		}

		// TODO: Should be removed in the future - replaced with toolbar prop
		if (this.options.toolbarForSessionDescription) {
			this.ckEditorOptions.linkShowAdvancedTab = false;
			this.ckEditorOptions.toolbar = sessionDescriptionToolbar;
		}

		// Set available toolbar actions
		if (this.options.toolbar) {
			// TODO: check what is this
			this.ckEditorOptions.linkShowAdvancedTab = false;
			this.ckEditorOptions.toolbar = this.options.toolbar;
		}

		const insertToolbarObject = insertInToolbar(this.options);
		this.ckEditorOptions.toolbar = [...this.ckEditorOptions.toolbar, insertToolbarObject];

		this.ckEditorOptions.extraPlugins = loadPlugins(this.options);

		if (this.options.bodyClass) {
			this.ckEditorOptions.bodyClass = this.options.bodyClass;
		}

		if (this.options.formatTags) {
			this.ckEditorOptions.format_tags = this.options.formatTags;
		}

		const removePluginsArr = removePlugins(this.options);
		if (!removePluginsArr.length && this.options.addImageUpload) {
			CKEDITOR.config.filebrowserUploadUrl = '/uploaded_images';
		}

		if (removePluginsArr.length) {
			this.ckEditorOptions.removePlugins = removePluginsArr.join(',');
		}
	}

	writeValue(data: any): void {
		const ckEditor = (window as any).CKEDITOR;
		if (!!ckEditor && !!ckEditor.instances[this.id]) {
			ckEditor.instances[this.id].setData(data);
		}
	}

	setHtml(html: string): void {
		const ckEditor = (window as any).CKEDITOR;
		if (!!ckEditor && !!ckEditor.instances[this.id]) {
			ckEditor.instances[this.id].insertHtml(html);
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {}

	private onChange: any = () => {};

	private onTouched: any = () => {};

	/**
	 * Clear all CK editor instances
	 */
	private clearInstances(): void {
		for (const id of Object.keys(CKEDITOR.instances)) {
			try {
				CKEDITOR.instances[id].destroy();
			} catch (e) {
				console.log('Failed to destroy editor instance :: ' + e);
			}
		}
	}

	/**
	 * Adds action presets
	 * eg. Used to set CUD preset action buttons (button that inserts text to editor)
	 * @param actions list of custom action presets
	 */
	private addCustomPlugin(plugins: LuCkEditorCustomPlugin[]): void {
		plugins.forEach(plugin => {
			// If plugin is already registered, update its init content
			// Else, register plugin
			if (CKEDITOR.plugins.registered[plugin.name]) {
				CKEDITOR.plugins.registered[plugin.name].init = plugin.init;
			} else {
				CKEDITOR.plugins.add(plugin.name, {
					...plugin
				});
			}
		});
	}
}

declare global {
	interface Window {
		executeDjImageUpload: () => void;
	}
}
